import type { HostOffer, ThreadOverview } from "../../../api/threads/threadDecoders";
import { acceptHostOffer, attachConnector, readAutomaticHost, requestHostRole } from "../../../api/threads/threadHostingApi";
import { readThread } from "../../../api/threads/threadsApi";
import { changeThreadSettings } from "../data/settingsChanges";
import { hostOfferDecided } from "./incomingHostOffers";
import { settingsWithChoice, type HostingChoice } from "./routeChoices";

/**
 * Where a Thread runs without asking (F55, decided 27 Oct after lunch). The server decides it
 * alone (`readAutomaticHost`, BA12-02): your own Thread runs on the connector and agent you
 * used last, or on the only ready one; with several ready and none used before, or a route
 * that lacks what the Thread's turns need, you choose. Why someone else's Thread still asks:
 * its agent would run on your PC, subscription and files for everyone in it (T-8, T-9), so
 * taking it over is your explicit step: the host's offer accepted, or your request approved
 * by the host.
 */

/**
 * The host's choice applied (T-8): a changed route or profile is a logged settings change.
 * Going live, or moving to another PC, the server writes it and the go-live as one step, so
 * nothing starts on the PC it leaves in between (TR-B1b-02). The server keeps it as the last used.
 */
export async function applyHostingChoice(thread: ThreadOverview, choice: HostingChoice): Promise<void> {
  const changed = settingsWithChoice(thread.settings, choice);
  if (thread.attachedConnector !== choice.connectorId || thread.state !== "live") {
    await attachConnector(thread.threadId, choice.connectorId, changed === null ? null : { settings: changed, lastEntryNumber: thread.lastEntryNumber });
  } else if (changed !== null) {
    await changeThreadSettings(thread.threadId, thread.lastEntryNumber, changed, thread.bindings);
  }
}

/**
 * Your own saved Thread runs where the server says: on `connectorId` when its start chose that
 * PC (F106), with the route it chose. `false` when it can't without your choice; the Thread
 * then stays saved.
 */
export async function runOnOwnConnector(threadId: string, connectorId: string | null = null): Promise<boolean> {
  const [thread, host] = await Promise.all([readThread(threadId), readAutomaticHost(threadId, undefined, connectorId)]);
  if (thread.state !== "saved" || host === null) return false;
  // One step, as Hosting orders it (TR-B1b-02, Sol R3 S6): the settings are written only if it goes live.
  await attachConnector(thread.threadId, host.connectorId, host.settings === null ? null : { settings: host.settings, lastEntryNumber: thread.lastEntryNumber });
  return true;
}

/**
 * Take over someone else's Thread (T-9): accept the host's offer with `choice`, then set the
 * route and profile it runs with. The acceptance is conditional on the entry the person saw.
 */
export async function takeOver(offerId: string, choice: HostingChoice, lastEntryNumber: number): Promise<void> {
  const taken = await acceptHostOffer(offerId, choice.connectorId, lastEntryNumber);
  hostOfferDecided(offerId);
  const changed = settingsWithChoice(taken.settings, choice);
  if (changed !== null) {
    await changeThreadSettings(taken.threadId, taken.lastEntryNumber, changed, taken.bindings);
  }
}

/** Take over where the server says; `false` when that needs your choice. */
export async function takeOverAutomatically(offerId: string, thread: ThreadOverview, lastEntryNumber: number): Promise<boolean> {
  const host = await readAutomaticHost(thread.threadId);
  if (host === null) return false;
  const taken = await acceptHostOffer(offerId, host.connectorId, lastEntryNumber);
  hostOfferDecided(offerId);
  if (host.settings !== null) {
    await changeThreadSettings(taken.threadId, taken.lastEntryNumber, host.settings, taken.bindings);
  }
  return true;
}

/**
 * Take over without an offer (F55): ask the host for their role, to run on `choice`, with the
 * settings the hosting form would set; the server sets them as yours once the host approves
 * with one click (T-9). Unanswered, the request expires and you may fork.
 */
export function askToTakeOver(thread: ThreadOverview, choice: HostingChoice): Promise<HostOffer> {
  return requestHostRole(thread.threadId, choice.connectorId, settingsWithChoice(thread.settings, choice) ?? thread.settings);
}

/** Ask to run it where the server says; `null` when that needs your choice. */
export async function askToTakeOverAutomatically(thread: ThreadOverview): Promise<HostOffer | null> {
  const host = await readAutomaticHost(thread.threadId);
  return host === null ? null : requestHostRole(thread.threadId, host.connectorId, host.settings ?? thread.settings);
}
